import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useParams,
  useNavigate,
} from 'react-router';
import moment from 'moment';
import get from 'lodash/get';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import isArray from 'lodash/isArray';
import {
  API,
  GlobalActions,
  ToastActions,
  NavigationHelper,
  OrderTypeHelper,
  FlightModel,
  FlightSummaryModel,
  SegmentModel,
  Field2 as Field,
  FlightStatus,
  Submenu,
  VibeModal,
  VibeTooltip,
  VibeAlert,
  VibeIcon,
  viCheckCircleOutline,
  viCloseCircleOutline,
  viArrowLeftCircleOutline,
  viArrowRight,
  viCopy,
  viCreate,
  viArchive,
  viUnarchive,
  viSave,
  color,
} from 'vibeguide';
import C, { lower } from 'constants';
import SubNavigation from '../../Header/SubNavigation';
import SegmentSidebar from '../Segments/Sidebar/SegmentSidebar';
import FlightInformation from './FlightInformation';
import AdInformation from './AdInformation';
import ApproverInformation from './ApproverInformation';
import FlightSegmentOverview from './FlightSegmentOverview';
import FlightAllocationOverview from './FlightAllocationOverview';
import FlightNotes from './FlightNotes';
import './FlightDetails.scss';

/**
 * Reset the confirmation modal dialog
 */
function resetConfirmModal() {
  return {
    show: false,
    title: '',
    description: '',
    confirmColor: '',
    confirmText: null,
    cancelText: null,
    onConfirm: () => {},
    onCancel: () => {},
  };
}

function FlightDetails({
  className = '',
  user,
  socket,
  currencies,
  setPanel,
  queueToast,
}) {
  let confirmationCode = null;

  const [flight, setFlight] = useState({
    data: new FlightModel(),
    summary: new FlightSummaryModel(),
    segments: [],
  });
  const [advertiserName, setAdvertiserName] = useState('');

  const [confirmModal, setConfirmModal] = useState(resetConfirmModal());
  const [disableConfirmButton, setDisableConfirmButton] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [showDenyModal, setShowDenyModal] = useState(false);
  const [denyReason, setDenyReason] = useState(null);
  const [denyApprovalType, setDenyApprovalType] = useState(null);

  // disable approve button if the flight is awaiting approval and has past the start date
  const [disableApprove, setDisableApprove] = useState(false);

  const history = useNavigate();
  const qs = NavigationHelper.getParams() || {};

  const {
    id,
    type,
  } = useParams();

  /**
   * Update Flight Data
   */
  const updateFlightData = (data) => {
    setFlight({
      ...flight,
      data: new FlightModel({
        ...flight.data,
        ...data,
      }),
    });

    setHasChanges(true);
  };

  /**
   * Get Flight Segments
   * Include flight summary segments if a new summary was fetched
   * Otherwise use the existing flight summary data
   */
  const getSegments = async (summarySegments) => {
    // if user cannot view segments skip
    if (!user.can('flight_segment.view')) {
      return [];
    }

    const params = NavigationHelper.getParams();
    const active = get(params, 'active') !== 'false';

    // get all segments so we can merge with flight summary data
    const response = await API.Flight.Segment.list(
      {
        flightId: id,
        filters: {
          active,
        },
        pageSize: -1,
      },
    );

    if (!summarySegments) {
      // no summary segments passed in, use existing summary
      summarySegments = get(flight, 'summary.segments', []);
    }

    // merge segment data from the flight summary with segments from the API
    const segmentItems = get(response, 'data', []).map((segment) => {
      // find the associated segment in the summary data
      const summarySegment = find(summarySegments, { _id: segment._id }) || {};

      // merge the two together
      return {
        ...new SegmentModel(segment),
        ...summarySegment,
      };
    });

    return segmentItems;
  };

  /**
   * Get Flight Data
   */
  const getFlightData = async () => {
    const flightResponse = await API.Flight.getById(id);
    return new FlightModel(flightResponse);
  };

  /**
   * Get Flight Summary
   */
  const getFlightSummary = async () => {
    const flightSummaryResponse = await API.Flight.summary(id);
    return new FlightSummaryModel(flightSummaryResponse);
  };

  /**
   * Get Flight Details
   */
  const getFlight = async () => {
    const flightData = await getFlightData();
    const flightSummaryData = await getFlightSummary();
    const flightSegments = await getSegments(flightSummaryData.segments);

    setFlight({
      ...flight,
      data: flightData,
      summary: flightSummaryData,
      segments: flightSegments,
    });

    if (flightData.advertiserId) {
      // only set the advertiser name if an ID is also set
      setAdvertiserName(flightData.advertiserName);
    }
  };

  /**
   * Get Tooltip for Create Order button
   */
  const getCreateOrderTooltip = () => {
    if (hasChanges) {
      return 'Please save changes before creating an order';
    }

    if (flight.segments.length === 0) {
      return `This ${lower(C.PROPOSAL)} must have ${lower(C.SEGMENTS)}`;
    }

    return null;
  };

  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    updateFlightData({
      [name]: value,
    });
  };

  const onChangeStartDate = (startDate) => {
    updateFlightData({
      startDate,
    });
  };

  const onChangeEndDate = (endDate) => {
    updateFlightData({
      endDate,
    });
  };

  const onChangeDropdown = ({
    name,
    value,
    // data,
  }) => {
    updateFlightData({
      [name]: value,
    });
  };

  const onChangeAdvertiser = (data) => {
    const {
      id,
      value,
    } = data;

    updateFlightData({
      advertiserId: id,
      advertiserName: value,
    });

    setAdvertiserName(value);
  };

  const onChangeApproverInformation = (data) => {
    updateFlightData(data);
  };

  /**
   * When the deny reason textarea is changed
   */
  const onChangeDenyReason = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    setDenyReason(value);

    if (!value) {
      // disable confirm button if no reason provided
      setDisableConfirmButton(true);
    } else {
      // enable confirm button
      setDisableConfirmButton(false);
    }
  };

  /**
   * When the sidebar is closed
   */
  const onCloseSidebar = () => {
    const url = NavigationHelper.updateParams({
      segmentId: null,
      flightId: null,
      type: null,
    }, {
      keepPage: true,
    });

    history(url);
  };

  /**
   * Toggle the archive button
   */
  const onToggleArchive = async () => {
    const flightSegments = await getSegments();

    setFlight({
      ...flight,
      segments: flightSegments,
    });
  };

  /**
   * Cancel the confirmation modal
   */
  const onCancelConfirmModal = () => {
    setConfirmModal(resetConfirmModal());
    // enable the confirm button
    setDisableConfirmButton(false);
  };

  /**
   * Cancel the deny flight modal
   */
  const onCancelDenyModal = () => {
    setShowDenyModal(false);
    setDenyReason(null);
    setDenyApprovalType(null);
  };

  /**
   * Confirm Inventory
   */
  const onConfirmInventory = async () => {
    onCancelConfirmModal();

    const response = await API.Flight.confirm(id);
    const responseType = get(response, '[0].type');
    const awaitingApproval = responseType === 'FLIGHT.SUBMITTED_FOR_APPROVAL';

    if (awaitingApproval) {
      getFlight();
    }
  };

  // TODO: Remove this function
  /**
   * Release (Unconfirm) Inventory
   */
  // const onReleaseInventory = async () => {
  //   onCancelConfirmModal();
  //   await API.Flight.unconfirm(id);
  // };

  /**
   * Archive Flight
   */
  const onArchiveFlight = async () => {
    onCancelConfirmModal();

    const archiveResponse = await API.Flight.deactivate(id);
    const success = get(archiveResponse, '[0].type') === 'FLIGHT.DEACTIVATED';

    // redirect to flights page after archiving a flight
    if (success) {
      history(`/ad-ops/${lower(C.PROPOSALS)}`);
    }
  };

  /**
   * Unarchive Flight
   */
  const onUnarchiveFlight = async () => {
    onCancelConfirmModal();

    const response = await API.Flight.reactivate({
      _id: id,
    });
    const success = get(response, '[0].type') === 'FLIGHT.REACTIVATED';

    // redirect to flights page after unarchiving a flight
    if (success) {
      history(`/ad-ops/${lower(C.PROPOSALS)}`);
    }
  };

  /**
   * Copy Flight
   */
  const onCopyFlight = async () => {
    onCancelConfirmModal();

    const response = await API.Flight.copy(id);
    const success = get(response, '[0].type') === 'FLIGHT.CREATED';

    // redirect to the copied flight
    if (success) {
      const copyFlightId = get(response, '[0].documentId');
      history(`/ad-ops/${lower(C.PROPOSAL)}/${copyFlightId}`);
    }
  };

  /**
   * Recall Flight
   */
  const onRecallFlight = async () => {
    onCancelConfirmModal();

    const response = await API.Flight.Approval.recall({ _id: id });
    const success = get(response, '[0].type') === 'FLIGHT.RECALLED';

    if (success) {
      getFlight();
    }
  };

  /**
   * Deny Flight
   */
  const onDenyFlight = async () => {
    const response = await API.Flight.Approval.requestChanges({
      _id: id,
      response: 'deny',
      comment: denyReason,
      approvalType: denyApprovalType,
    });
    const success = get(response, '[0].type') === 'FLIGHT.DENIED';

    if (success) {
      onCancelDenyModal();
      getFlight();
    }
  };

  /**
   * Approve Flight
   */
  const onApproveFlight = async (approvalType) => {
    onCancelConfirmModal();

    const response = await API.Flight.Approval.approve({
      _id: id,
      response: 'accept',
      approvalType,
    });
    const responseType = get(response, '[0].type');
    const success = responseType === 'FLIGHT.APPROVED'
      || responseType === 'FLIGHT.PARTIALLY_APPROVED';

    if (success) {
      getFlight();
    }
  };

  /**
   * User clicks Archive Flight
   */
  const onClickArchive = () => {
    setConfirmModal({
      show: true,
      title: `Archive ${C.PROPOSAL}`,
      description: `Are you sure you want to archive ${get(flight, 'data.name', 'Unknown')}?`,
      confirmColor: 'error',
      onConfirm: onArchiveFlight,
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * User clicks Unarchive Flight
   */
  const onClickUnarchive = () => {
    setConfirmModal({
      show: true,
      title: `Unarchive ${C.PROPOSAL}`,
      description: `Are you sure you want to unarchive ${get(flight, 'data.name', 'Unknown')}?`,
      confirmColor: 'warning',
      onConfirm: onUnarchiveFlight,
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * User clicks Copy Flight
   */
  const onClickCopy = () => {
    setConfirmModal({
      show: true,
      title: `Copy ${C.PROPOSAL}`,
      description: `Are you sure you want to copy ${get(flight, 'data.name', 'Unknown')}?`,
      confirmColor: 'warning',
      onConfirm: onCopyFlight,
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * User clicks Recall Flight
   */
  const onClickRecall = () => {
    setConfirmModal({
      show: true,
      title: `You are recalling this ${lower(C.PROPOSAL)}`,
      description: `This will recall the ${lower(C.PROPOSAL)} back to your ${lower(C.PROPOSALS)} list.
        This cannot be undone.`,
      confirmColor: 'error',
      onConfirm: onRecallFlight,
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * User clicks Deny or Override Deny
   */
  const onClickDeny = (approvalType) => {
    setDisableConfirmButton(true);
    setDenyReason(null);
    setDenyApprovalType(approvalType);
    setShowDenyModal(true);
  };

  /**
   * User clicks Approve or Override Approve
   */
  const onClickApprove = (approvalType) => {
    const isUnanimous = flightData.partnerApproval?.style === 'unanimous';
    const isAnyone = flightData.partnerApproval?.style === 'anyone';
    // get all approvers (except yourself)
    const approvers = (flightData.partnerApproval?.approvers || []).filter(approver => approver._id !== user._id);
    // check if all other approvers have already approved
    const approvedCount = approvers.filter(approver => approver.status === 'approved')?.length || 0;

    const description = isAnyone || (isUnanimous && approvers.length === approvedCount)
      // this is the last approver needed to create the order
      ? 'An order will now be created and viewable on the order dashboard.'
      // let the user know more approvals are required
      : 'The order will not be created until the last person in your group has approved.';

    setConfirmModal({
      show: true,
      title: approvalType === 'override-partner'
        ? `You are overriding approval for this ${lower(C.PROPOSAL)}`
        : `You are approving this ${lower(C.PROPOSAL)}`,
      description: approvalType === 'override-partner'
        ? `Overriding the approval process will immediately approve this ${lower(C.PROPOSAL)}.`
        : description,
      confirmColor: 'success',
      onConfirm: () => onApproveFlight(approvalType),
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * User clicks Confirm Inventory
   */
  const onClickConfirmInventory = () => {
    setConfirmModal({
      show: true,
      title: 'Confirm Inventory',
      description: (
        <div>
          Confirm the {lower(C.PROPOSAL)} and associated {lower(C.SEGMENTS)}?<br />
          This process may take several minutes.
        </div>
      ),
      confirmColor: 'warning',
      onConfirm: onConfirmInventory,
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * User clicks Create Order but it needs approval
   */
  const onClickCreateOrderApproval = () => {
    setConfirmModal({
      show: true,
      title: `You are submitting this ${lower(C.PROPOSAL)} for approval`,
      description: (
        <div>
          This {lower(C.PROPOSAL)} will not become an order unless approved.
          If denied, it will go back into your unconfirmed {lower(C.PROPOSALS)} list.<br /><br />

        {C.PROPOSALS} pending approval are more likely to experience changes in inventory.
        </div>
      ),
      confirmColor: 'warning',
      onConfirm: onConfirmInventory,
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * When the user confirms the update to the flight (when applicable)
   */
  const onConfirmUpdateFlight = async () => {
    onCancelConfirmModal();
    await onSave();

    // fetch segments again to show reflected changes
    const flightSegments = await getSegments();

    setFlight({
      ...flight,
      segments: flightSegments,
    });
  };

  // TODO: Remove this function
  /**
   * User clicks Release Inventory
   */
  // const onClickReleaseInventory = () => {
  //   setConfirmModal({
  //     show: true,
  //     title: 'Release Inventory',
  //     description: (
  //       <div>
  //         Unconfirm the flight and associated segments?<br />
  //         This process may take several minutes.
  //       </div>
  //     ),
  //     confirmColor: 'warning',
  //     onConfirm: onReleaseInventory,
  //     onCancel: onCancelConfirmModal,
  //   });
  // };

  /**
   * Listen for socket events
   */
  const onApiEvent = async (e) => {
    switch (e.type) {
      case 'SOCKET.SUBSCRIBE_FAILED': {
        const topic = get(e, 'data.topic');
        const flightId = get(e, 'data.id');
        const error = get(e, 'data.error');
        // only re-try subscribing to a flight if the subscribed flight
        // doesn't match the current fetched flight
        // this is to prevent multiple requests to the API for the same flight
        if (error.includes('Already subscribed') && flightId === id) {
          break;
        }

        if (
          (topic === 'flight' && flightId === id)
          && (flightId !== flight.data._id)
        ) {
          // retry subscribing to the flight after a few seconds
          setTimeout(() => {
            socket.emit('subscribe', {
              topic: 'flight',
              id,
            });
          }, 3000);
        }
        break;
      }

      // When a flight is calculating
      case 'FLIGHT.COMPUTING': {
        const flightData = await getFlightData();
        const flightSegments = await getSegments();

        setFlight({
          ...flight,
          data: flightData,
          // do not request a new summary until after the flight is computed
          summary: flight.summary,
          segments: flightSegments,
        });
        break;
      }

      case 'FLIGHT.CONFIRM_QUEUED':
      case 'FLIGHT.CONFIRM_STARTED':
      case 'FLIGHT.UNCONFIRM_QUEUED':
      case 'FLIGHT.UNCONFIRM_STARTED': {
        const flightData = await getFlightData();
        const flightSegments = await getSegments();

        setFlight({
          ...flight,
          data: flightData,
          summary: new FlightSummaryModel(),
          segments: flightSegments,
        });
        break;
      }

      case 'FLIGHT.COMPUTED':
      case 'FLIGHT.CONFIRM_FAILED':
      case 'FLIGHT.UNCONFIRMED':
      case 'FLIGHT.UNCONFIRM_FAILED':
      case 'FLIGHT.RECOVERED': {
        getFlight();
        break;
      }

      case 'FLIGHT.CONFIRMED': {
        history(`../order/${id}/details`);
        break;
      }

      default:
        // console.warn('Event', e);
        break;
    }
  };

  /**
 * When the socket reconnects
 */
  const onSocketReconnect = () => {
    if (flight.data._id) {
      socket.emit('subscribe', {
        topic: 'flight',
        id: flight.data._id,
      });

      getFlight();
    }
  };

  /**
   * User clicks Save Flight
   */
  const onSave = async () => {
    try {
      let options = {};

      if (confirmationCode) {
        options = { headers: { 'VAPI-ConfirmationCode': confirmationCode } };
        // reset the confirmation code
        confirmationCode = null;
      }

      const flightResponse = await API.Flight.update(flight.data, options);
      const confirmationRequired = !isUndefined(get(flightResponse, '[0].vapiConfirmationCode'));
      const success = get(flightResponse, '[0].type') === 'FLIGHT.UPDATED';

      if (confirmationRequired) {
        confirmationCode = get(flightResponse[0], 'vapiConfirmationCode');
        const originalCurrency = get(flightResponse[0], 'data.originalCurrencyCode');
        const modifiedCurrency = get(flightResponse[0], 'data.modifiedCurrencyCode');
        const formatOriginalCurrency = find(currencies, { key: originalCurrency })?.label || originalCurrency;
        const formatModifiedCurrency = find(currencies, { key: modifiedCurrency })?.label || modifiedCurrency;

        setConfirmModal({
          show: true,
          title: 'Currency Update',
          description: (
            <div>
              Changing a {lower(C.PROPOSALS)} currency affects all associated {lower(C.SEGMENTS)}.
              <br />
              Are you sure you want to change the currency of the {C.PROPOSAL}?

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 16,
                }}
              >
                {formatOriginalCurrency}

                <VibeIcon
                  icon={viArrowRight}
                  color={color.secondary}
                  size={16}
                />

                {formatModifiedCurrency}
              </div>
            </div>
          ),
          confirmColor: 'warning',
          confirmText: 'Update Currency',
          onConfirm: onConfirmUpdateFlight,
          onCancel: onCancelConfirmModal,
        });
        return;
      }

      queueToast({
        type: 'info',
        title: 'Saving...',
        allowClose: true,
      });

      if (success) {
        setHasChanges(false);

        queueToast({
          type: 'success',
          title: 'Saved!',
          allowClose: true,
        });

        // tell listening components the object was saved
        document.dispatchEvent(new Event('onSaveFlight'));
      } else {
        console.error(`Error saving ${lower(C.PROPOSAL)} to API`, flightResponse);

        queueToast({
          type: 'error',
          title: `Error Saving ${C.PROPOSAL}`,
          timeout: 10,
          allowClose: true,
        });
      }
    } catch (err) {
      console.error(`Error saving ${lower(C.PROPOSAL)}`, err);

      queueToast({
        type: 'error',
        title: `Error Saving ${C.PROPOSAL}`,
        timeout: 10,
        allowClose: true,
      });
    }
  };

  const sidebar = (type, segmentId = null) => {
    setPanel({
      show: true,
      backdrop: type === 'new',
      width: 400,
      children: (
        <SegmentSidebar
          type="flight"
          segmentId={segmentId}
          routeId={id}
          isNew={type === 'new'}
        />
      ),
      onClose: onCloseSidebar,
    });
  };

  /**
   * Check if flight approval information is complete
   */
  const validateApprovalInfo = () => {
    // Approver information is valid when approval isn't required
    // or when the approval style is set to 'anyone'
    // or when there is at least one approver selected
    if (!flightData.approvalRequired
      || get(flightData, 'partnerApproval.style') === 'anyone'
      || get(flightData, 'partnerApproval.approvers.length', 0) > 0
    ) {
      return true;
    }

    return false;
  };

  /**
   * Get the buttons to use for the flight
   */
  const getFlightButtons = () => {
    const confirmed = flightData.status === 'confirmed';
    const unconfirmed = flightData.status === 'unconfirmed';
    const awaitingApproval = flightData.status === 'awaiting-approval';
    let flightButtons = [];

    if (!awaitingApproval) {
      // if not set to automatically publish, is all the approval information filled out
      const approvalInfoComplete = validateApprovalInfo();

      // disable buttons when flight is not in a confirmed or unconfirmed state
      const disableButtons = !confirmed && !unconfirmed;
      const disableCopy = disableButtons;
      const disableCreateOrder = disableButtons
        || !approvalInfoComplete
        || hasChanges
        || flightSegments.length === 0;

      const disableSave = !flightData.name
        || !flightData.currencyCode
        || !flightData.advertiserId
        || !flightData.adProvider
        || !flightData.deliverySystem
        || !approvalInfoComplete
        || !hasChanges;


      // flight buttons to show
      flightButtons = flightData.active
        ? [
          {
            text: 'Copy',
            userCan: [
              'flight.create',
            ],
            icon: viCopy,
            color: 'primary',
            disabled: disableCopy,
            onClick: onClickCopy,
          },
          {
            text: 'Save',
            userCan: [
              'flight.modify',
            ],
            color: color.primary,
            icon: viSave,
            disabled: disableSave || disableButtons,
            onClick: onSave,
          },
          {
            text: 'Create Order',
            userCan: [
              'flight.confirm',
            ],
            color: color.primary,
            icon: viCreate,
            disabled: disableCreateOrder,
            tooltip: getCreateOrderTooltip(),
            onClick: flightData.approvalRequired
              // submit for approval modal
              ? onClickCreateOrderApproval
              // create order confirmation modal
              : onClickConfirmInventory,
          },
        ]
        : [];

      if (flightData.active) {
        // show archive flight button only when unconfirmed
        flightButtons.push({
          text: 'Archive',
          userCan: [
            'flight.delete',
          ],
          color: 'error',
          icon: viArchive,
          disabled: disableButtons,
          onClick: onClickArchive,
        });
      } else {
        // show unarchive flight button
        flightButtons.push({
          text: 'Unarchive',
          userCan: [
            'flight.delete',
          ],
          color: 'error',
          icon: viUnarchive,
          onClick: onClickUnarchive,
        });
      }
    } else {
      // Flight is Awaiting Approval
      const approvers = flightData.partnerApproval?.approvers || [];

      const isApprover = find(approvers, { _id: user._id }) !== undefined
        || (flightData.partnerApproval?.style === 'anyone'
            && flightData.partnerId === user.partnerId
            && user.can('flight.approve'));

      flightButtons = [
        {
          text: 'Recall',
          userAnyOf: ['flight.create', 'flight.approve', 'flight.global_approver'],
          icon: viArrowLeftCircleOutline,
          color: color.primary,
          onClick: onClickRecall,
        },
        {
          text: 'Deny',
          userCan: ['flight.approve'],
          icon: viCloseCircleOutline,
          color: color.error,
          show: isApprover,
          onClick: () => onClickDeny('partner'),
        },
        {
          text: 'Override Deny',
          userCan: ['flight.global_approver'],
          icon: viCloseCircleOutline,
          color: color.error,
          onClick: () => onClickDeny('override-partner'),
        },
        {
          text: 'Approve',
          userCan: ['flight.approve'],
          icon: viCheckCircleOutline,
          color: color.success,
          show: isApprover,
          disabled: disableApprove,
          onClick: () => onClickApprove('partner'),
        },
        {
          text: 'Override Approve',
          userCan: ['flight.global_approver'],
          icon: viCheckCircleOutline,
          color: color.success,
          disabled: disableApprove,
          onClick: () => onClickApprove('override-partner'),
        },
      ];
    }

    return flightButtons;
  };

  useEffect(() => {
    const isNew = qs.type === 'new';
    const isView = qs.segmentId && qs.type !== 'new';

    if (isNew || isView) {
      sidebar(qs.type, qs.segmentId);
    }
  }, [qs.type, qs.segmentId]);

  /**
   * When connected to the socket, listen for flight events
   * Resubscribe to the socket events when the flight data is fetched
   */
  useEffect(() => {
    socket.on('connect', onSocketReconnect);

    if (socket.connected) {
      socket.off('VAPI_EVENT', onApiEvent);
      socket.on('VAPI_EVENT', onApiEvent);
    }

    // when component unmounts
    return () => {
      socket.off('VAPI_EVENT', onApiEvent);
      socket.off('connect', onSocketReconnect);
    };
  }, [socket.connected, flight.data._id, id]);

  /**
   * When connected to the socket, listen for flight events
   * Initially subscribe to the flight from the page ID
   * Resubscribe if the flight ID changes (when a flight is copied)
   */
  useEffect(() => {
    if (socket.connected) {
      socket.off('VAPI_EVENT', onApiEvent);
      socket.on('VAPI_EVENT', onApiEvent);

      socket.emit('subscribe', {
        topic: 'flight',
        id,
      });
    }

    // when component unmounts
    return () => {
      socket.off('VAPI_EVENT', onApiEvent);

      socket.emit('unsubscribe', {
        topic: 'flight',
        id,
      });
    };
  }, [socket.connected, id]);

  /**
   * When a flight requires approval but has a start date in the past
   */
  useEffect(() => {
    if (flight.data.status === 'awaiting-approval') {
      const today = moment().startOf('day');
      const started = today.isAfter(moment(flight.data.startDate));

      if (started) {
        setConfirmModal({
          show: true,
          title: `This ${lower(C.PROPOSAL)} has a start date in the past.`,
          description: `${C.PROPOSALS} with a start date in the past cannot be approved.
            Please deny the request to allow the requester to adjust the dates, 
            or they can recall the ${lower(C.PROPOSAL)} to make the necessary changes.`,
          confirmText: 'Ok',
          cancelText: 'Close',
          onConfirm: onCancelConfirmModal,
          onCancel: onCancelConfirmModal,
        });

        setDisableApprove(true);
      }
    }
  }, [flight.data.status, flight.data.startDate]);

  /**
   * When the flight ID changes fetch the new flight data
   */
  useEffect(() => {
    if (id) {
      getFlight();
    }
  }, [id]);

  const flightData = flight.data;
  const flightSummary = flight.summary;
  const flightSegments = flight.segments;

  // can view partner data
  const isViewOnly = flightData.partnerAccessLevel === 'view-all'
    || flightData.partnerAccessLevel === 'view-limited'
    || flightData.partnerAccessLevel === 'none';

  // only render when the flight is fetched
  if (!flightData._id) {
    return null;
  }

  const orderTypeItem = find(OrderTypeHelper.list(), { value: flightData.orderType });

  /**
   * Get the page content
   */
  const getPageContent = () => {
    const canModifyFlight = user.can('flight.modify') && flightData.active;
    const unconfirmed = flightData.status === 'unconfirmed';
    const awaitingApproval = flightData.status === 'awaiting-approval';
    // disable fields if the flight is awaiting approval, no unconfirmed, or the user can't modify the flight
    const disabled = awaitingApproval || !unconfirmed || !canModifyFlight;

    switch (type) {
      case 'segments':
        return (
          <FlightSegmentOverview
            flightData={flightData}
            flightSummary={flightSummary}
            flightSegments={flightSegments}
            isViewOnly={isViewOnly}
            onToggleArchive={onToggleArchive}
          />
        );

      case 'allocations':
        return (
          <FlightAllocationOverview
            orderType={flightData.orderType}
            flightId={flightData._id}
            currencyCode={flightData.currencyCode}
          />
        );

      case 'overview':
      default:
        return (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 24,
                margin: '0 2rem',
              }}
            >
              <div
                style={{
                  width: '50%',
                }}
              >
                <FlightInformation
                  flightData={flightData}
                  title={`${C.PROPOSAL} Information`}
                  disabled={disabled}
                  onChange={onChange}
                />

                <AdInformation
                  flightData={flightData}
                  advertiserName={advertiserName}
                  title="Ad Information"
                  disabled={disabled}
                  onChange={onChange}
                  onChangeDropdown={onChangeDropdown}
                  onChangeAdvertiser={onChangeAdvertiser}
                  onChangeStartDate={onChangeStartDate}
                  onChangeEndDate={onChangeEndDate}
                />
              </div>

              {!isViewOnly && (
                <div
                  style={{
                    width: '50%',
                  }}
                >
                  <ApproverInformation
                    flightData={flightData}
                    flightSegments={flightSegments}
                    title="Approver Information"
                    onChange={onChangeApproverInformation}
                  />

                  <FlightNotes
                    flightId={flightData._id}
                    notes={flightData.activity}
                    canAddNote={user.can('flight.modify')}
                  />
                </div>
              )}
            </div>
          </>
        );
    }
  };

  return (
    <div className={classNames('FlightDetails', className)}>
      <SubNavigation
        menuName={`${C.PROPOSAL} Menu`}
        isDropMenu
        title={(
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {get(flightSummary, 'warnings.length', 0) > 0 && (
              <VibeAlert
                style={{
                  fontSize: 12,
                }}
                severity="warning"
                alerts={flightSummary.warnings || []}
                size={24}
                iconOnly
              />
            )}

            {orderTypeItem && (
              <VibeIcon
                icon={orderTypeItem.icon}
                color={color.primary}
                size={24}
                tooltip={orderTypeItem.name}
              />
            )}

            <VibeTooltip title={flightData.name}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {flightData.name}
              </div>
            </VibeTooltip>

            <FlightStatus
              status={flightData.status}
              style={{
                marginLeft: 8,
              }}
            />
          </div>
        )}
        style={{
          borderBottom: `1px solid ${color.whiteSmoke}`,
        }}
        buttons={!isViewOnly
          ? getFlightButtons()
          : []}
        disabled={flightData.flightStage === 'order'}
      />

      <Submenu
        rootUrl={`/ad-ops/${lower(C.PROPOSAL)}/${flightData._id}`}
        matchParameter="tab"
        items={[
          {
            name: 'Overview',
            show: true,
            enabled: user.can('flight.view'),
          },
          {
            name: 'Segments',
            show: true,
            enabled: user.can('flight_segment.view'),
          },
          {
            name: 'Allocations',
            icon: isArray(flightSummary.warnings) && flightSummary?.warnings?.length > 0 ? (
              <VibeAlert
                severity="warning"
                alerts={flightSummary.warnings || []}
                size={24}
                iconOnly
              />
            ) : null,
            show: flightData.orderType !== 'always-on-pmp',
            enabled: user.can('flight.view'),
          },
        ]}
      />

      <div
        style={{
          flexGrow: 1,
          paddingBottom: 0,
          marginTop: 16,
        }}
      >
        {getPageContent()}
      </div>

      <VibeModal
        type="confirm"
        show={confirmModal.show}
        confirmProps={{
          color: confirmModal.confirmColor,
          disabled: disableConfirmButton,
          text: confirmModal.confirmText,
        }}
        cancelProps={{
          text: confirmModal.cancelText,
        }}
        title={confirmModal.title}
        text={confirmModal.description}
        onConfirm={confirmModal.onConfirm}
        onClose={confirmModal.onCancel}
      />

      <VibeModal
        type="confirm"
        show={showDenyModal}
        confirmProps={{
          color: 'error',
          disabled: disableConfirmButton,
        }}
        title={`Denied Reason${denyApprovalType === 'override-partner' ? ' (Override)' : ''}`}
        onConfirm={() => onDenyFlight(denyApprovalType)}
        onClose={onCancelDenyModal}
      >
        <div>
          Please provide a reason below. Denying this {lower(C.PROPOSAL)} will send back to the original requester
          and stay in an unconfirmed status.

          <Field
            type="textarea"
            style={{
              marginTop: 24,
              height: 100,
            }}
            placeholder="Enter a description..."
            value={denyReason}
            autoFocus
            onChange={onChangeDenyReason}
          />
        </div>
      </VibeModal>
    </div>
  );
}

FlightDetails.propTypes = {
  className: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
    socket: state.socket.connection,
    currencies: state.currency.list,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
  queueToast: ToastActions.queueToast,
};

export default connect(mapStateToProps, mapDispatchToProps)(FlightDetails);
